import React, { useEffect, useState } from 'react';
import FieldContainer from 'components/layout/FieldContainer';
import { getValidationSchema } from 'utils/validationProfExpSchemaGenerator';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import { Alert, Snackbar } from '@mui/material';
import styles from '../ProfBackground.module.scss';
import EducationItemEditing from './EducationItemEditing';
import ProfBackgroundItem from '../ProfBackgroundItem';
import { SAVE_USER_EDUCATION, DELETE_USER_EDUCATION } from '../../../../api';
import moment from 'moment';
import PartialLoader from 'components/layout/PartialLoader';

const EDUCATION = 'Education';
const EMPTY_TEXT = 'Add your education';

const Notification = React.forwardRef((props, ref) => (
  <Alert elevation={6} ref={ref} variant="filled" {...props} />
));

const Education = ({ userProps, isCurrentUser, setUserData }) => {
  const [isEditing, setIsEditing] = useState(false);
  const validationSchema = getValidationSchema('educations');
  const formOptions = { resolver: yupResolver(validationSchema) };
  const formMethods = useForm(formOptions, { mode: 'all' });
  const [educations, setEducations] = useState([]);
  const [educationsList, setEducationsList] = useState([]);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveUserEducation] = useMutation(SAVE_USER_EDUCATION);
  const [deleteUserEducation] = useMutation(DELETE_USER_EDUCATION);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    setError,
  } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'education',
    keyName: 'key',
  });

  useEffect(() => {
    if (!isEditing) {
      setValue('education', []);
    } else if (educationsList && educationsList.length) {
      educationsList.map((item) => {
        append({
          id: item.id,
          establishment: item.establishment,
          study: item.study,
          start_date: moment(item.start_date).toISOString(),
          end_date: moment(item.end_date).toISOString(),
        });
        return item;
      });
    }
  }, [isEditing]);

  useEffect(() => {
    if (userProps?.educations && userProps.educations.length) {
      setEducationsList(userProps.educations);
    } else {
      setEducationsList([]);
    }
  }, [userProps]);

  const handleAdd = () => {
    append({
      establishment: '',
      study: '',
      start_date: null,
      end_date: null,
    });
  };

  const handleDelete = async (index) => {
    if (fields[index].id) {
      const res = await deleteUserEducation({ variables: { id: fields[index].id } });

      if (res.data) {
        remove(index);
        const newEducationsList = educationsList.filter((item, i) => index !== i);
        setUserData({ ...userProps, educations: newEducationsList });
        return true;
      }
    }
    remove(index);
    return true;
  };

  const handleNotificationClose = () => {
    setIsNotificationOpen(false);
  };

  const onFormSubmit = async (data) => {
    setLoading(true);

    const educationList = [];
    data.education.map((item) => {
      const itemData = {
        id: item.id,
        establishment: item.establishment,
        study: item.study,
        start_date: item.start_date,
        end_date: item.end_date,
        is_present: item.isPresent,
      };

      itemData.start_date = moment(item.start_date).format('YYYY-MM-DD');
      if (itemData.end_date) {
        itemData.end_date = moment(item.end_date).format('YYYY-MM-DD');
      }

      educationList.push(itemData);
      return item;
    });

    const userEducation = await saveUserEducation({ variables: { input: educationList } });

    if (userEducation?.data?.saveUserEducation?.educations) {
      setIsNotificationOpen(true);
      setIsEditing(false);
      setUserData({ ...userProps, educations: userEducation.data.saveUserEducation.educations });
    }

    setLoading(false);
  };

  return (
    <>
      {isCurrentUser ? (
        <FieldContainer
          key="educations"
          objKey="educations"
          title={EDUCATION}
          className={styles.contentBox}
          isCurrentUser={isCurrentUser}
          setIsEditingProfile={setIsEditing}
          isEditingProfile={isEditing}
          addProf={() => handleAdd()}
          formId="education"
          containEditFieldButton
          isProfBackground
        >
          {educationsList.length === 0 && !isEditing && (
            <div className={styles.emptyText}>{EMPTY_TEXT}</div>
          )}
          {fields.length > 0 || (educationsList && educationsList.length) ? (
            <FormProvider {...formMethods}>
              <form id="education" onSubmit={handleSubmit(onFormSubmit)}>
                {isEditing &&
                  fields?.map((exp, index) => (
                    <EducationItemEditing
                      key={exp.key}
                      formName="education"
                      index={index}
                      profProps={exp}
                      formTitle="Educations"
                      data={educations}
                      setData={setEducations}
                      control={control}
                      handleDelete={handleDelete}
                      setValue={setValue}
                      errors={errors}
                      isNew={!exp.id}
                      study={exp.study}
                      establishment={exp.establishment}
                      setError={setError}
                    />
                  ))}
                {!isEditing &&
                  educationsList.map((exp, index) => (
                    <ProfBackgroundItem
                      key={exp.id}
                      index={index}
                      title={exp.study}
                      subTitle={exp.establishment}
                      timeInterval={`${moment(exp.start_date).format('MM.YYYY')} - ${
                        exp.end_date ? moment(exp.end_date).format('MM.YYYY') : 'Present'
                      }`}
                    />
                  ))}
              </form>
            </FormProvider>
          ) : null}
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={isNotificationOpen}
            onClose={handleNotificationClose}
            autoHideDuration={6000}
          >
            <Notification onClose={handleNotificationClose} severity="success">
              Educations Updated Successfully!
            </Notification>
          </Snackbar>
        </FieldContainer>
      ) : educationsList.length !== 0 ? (
        <FieldContainer
          key="educations"
          objKey="educations"
          title={EDUCATION}
          className={styles.contentBox}
          isCurrentUser={isCurrentUser}
          setIsEditingProfile={setIsEditing}
          isEditingProfile={isEditing}
          addProf={() => handleAdd()}
          formId="education"
          containEditFieldButton
          isProfBackground
        >
          {fields.length > 0 || (educationsList && educationsList.length) ? (
            <FormProvider {...formMethods}>
              <form id="education" onSubmit={handleSubmit(onFormSubmit)}>
                {isEditing &&
                  fields?.map((exp, index) => (
                    <EducationItemEditing
                      key={exp.key}
                      formName="education"
                      index={index}
                      profProps={exp}
                      formTitle="Educations"
                      data={educations}
                      setData={setEducations}
                      control={control}
                      handleDelete={handleDelete}
                      setValue={setValue}
                      errors={errors}
                      isNew={!exp.id}
                    />
                  ))}
                {!isEditing &&
                  educationsList.map((exp, index) => (
                    <ProfBackgroundItem
                      key={exp.id}
                      index={index}
                      title={exp.study}
                      subTitle={exp.establishment}
                      timeInterval={`${moment(exp.start_date).format('MM.YYYY')} - ${
                        exp.end_date ? moment(exp.end_date).format('MM.YYYY') : 'Present'
                      }`}
                    />
                  ))}
              </form>
            </FormProvider>
          ) : null}
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={isNotificationOpen}
            onClose={handleNotificationClose}
            autoHideDuration={6000}
          >
            <Notification onClose={handleNotificationClose} severity="success">
              Educations Updated Successfully!
            </Notification>
          </Snackbar>
        </FieldContainer>
      ) : null}
      <PartialLoader loading={loading} isLightContainer />
    </>
  );
};

export default Education;
